import { useCallback, useEffect, useRef } from 'react';

import { useAtom } from 'jotai';
import { useSetAtom } from 'jotai';

import { addPollToHistoryAtom } from './pollHistoryAtom';
import { pollRegistry } from './pollRegistry';
import {
  getPollingInterval,
  hasExceededMaxDuration,
  pollsAtom,
} from './pollsAtom';
import { DEFAULT_POLLING_STRATEGY, PollStatus } from './types';

const toastTrackingRef = new Set<string>();

export function clearPollToastTracking(pollId: string) {
  toastTrackingRef.delete(pollId);
}

export function useGlobalPolling() {
  const [polls, dispatch] = useAtom(pollsAtom);
  const addToHistory = useSetAtom(addPollToHistoryAtom);
  const timeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map()
  );

  const clearPollTimeout = useCallback((pollId: string) => {
    const timeout = timeoutsRef.current.get(pollId);
    if (timeout) {
      clearTimeout(timeout);
      timeoutsRef.current.delete(pollId);
    }
  }, []);

  const pollStatus = useCallback(
    async (pollId: string) => {
      console.debug('[GlobalPolling] polling called for:', pollId);

      try {
        const poll = polls.find((j) => j.id === pollId);

        if (!poll) {
          clearPollTimeout(pollId);
          return;
        }

        const config = pollRegistry.get(poll.type);
        if (!config) {
          dispatch({ type: 'remove', pollId });
          clearPollTimeout(pollId);
          return;
        }

        const strategy = {
          ...DEFAULT_POLLING_STRATEGY,
          ...config.pollingStrategy,
        };

        if (hasExceededMaxDuration(poll, strategy.maxDuration)) {
          dispatch({
            type: 'updateStatus',
            pollId,
            status: PollStatus.Timeout,
          });

          // Add to history before removing from active polls if preserveHistory is true
          if (poll.preserveHistory !== false) {
            addToHistory({
              poll,
              status: PollStatus.Timeout,
              error: 'Polling timeout exceeded',
            });
          }

          dispatch({ type: 'remove', pollId });
          clearPollTimeout(pollId);

          if (config.onTimeout && !toastTrackingRef.has(pollId)) {
            config.onTimeout(poll);
            toastTrackingRef.add(pollId);
          }
          return;
        }

        dispatch({ type: 'updateLastPolled', pollId });
        dispatch({ type: 'incrementPollCount', pollId });

        const result = await config.pollFn(poll);

        dispatch({ type: 'updateData', pollId, data: result.data });

        if (result.shouldContinuePolling) {
          const interval = getPollingInterval(poll, strategy);
          // Clear any existing timeout before scheduling a new one
          clearPollTimeout(pollId);
          const timeout = setTimeout(() => pollStatus(pollId), interval);
          timeoutsRef.current.set(pollId, timeout);
        } else {
          dispatch({ type: 'updateStatus', pollId, status: result.status });

          // Add to history before removing from active polls if preserveHistory is true
          if (poll.preserveHistory !== false) {
            addToHistory({
              poll: { ...poll, status: result.status, data: result.data },
              status: result.status,
              error: result.error,
            });
          }

          if (result.status === PollStatus.Completed) {
            if (config.onComplete) {
              config.onComplete(poll, result);
            }
          } else if (result.status === PollStatus.Failed) {
            if (config.onFailure && !toastTrackingRef.has(pollId)) {
              config.onFailure(
                poll,
                new Error(result.error || 'Unknown error')
              );
              toastTrackingRef.add(pollId);
            }
          }

          // Remove from active polls after saving to history
          dispatch({ type: 'remove', pollId });
          clearPollTimeout(pollId);
        }
      } catch (error) {
        console.error(`[GlobalPolling] Error polling ${pollId}:`, error);

        const poll = polls.find((j) => j.id === pollId);
        if (poll) {
          const config = pollRegistry.get(poll.type);
          const errorMessage =
            error instanceof Error ? error.message : 'Unknown error';

          dispatch({
            type: 'updateStatus',
            pollId,
            status: PollStatus.Failed,
            error: errorMessage,
          });

          // Add to history before removing from active polls if preserveHistory is true
          if (poll.preserveHistory !== false) {
            addToHistory({
              poll: {
                ...poll,
                status: PollStatus.Failed,
                error: errorMessage,
              },
              status: PollStatus.Failed,
              error: errorMessage,
            });
          }

          if (config?.onFailure && !toastTrackingRef.has(pollId)) {
            config.onFailure(poll, error as Error);
            toastTrackingRef.add(pollId);
          }
        }

        dispatch({ type: 'remove', pollId });
        clearPollTimeout(pollId);
      }
    },
    [polls, dispatch, clearPollTimeout, addToHistory]
  );

  useEffect(() => {
    const timeouts = timeoutsRef.current;

    polls.forEach((poll) => {
      if (!timeoutsRef.current.has(poll.id)) {
        const config = pollRegistry.get(poll.type);
        if (!config) {
          return;
        }

        const strategy = {
          ...DEFAULT_POLLING_STRATEGY,
          ...config.pollingStrategy,
        };

        const timeSinceLastPoll = Date.now() - poll.lastPolledAt;
        const interval = getPollingInterval(poll, strategy);
        const initialDelay = Math.max(0, interval - timeSinceLastPoll);

        const timeout = setTimeout(() => {
          pollStatus(poll.id);
        }, initialDelay);
        timeoutsRef.current.set(poll.id, timeout);
      }
    });

    return () => {
      timeouts.forEach((timeout) => clearTimeout(timeout));
      timeouts.clear();
    };
  }, [polls, pollStatus]);

  return null;
}
